import { Box, IconButton, Stack, Tooltip } from "@mui/material";
import AddHomeWorkOutlinedIcon from "@mui/icons-material/AddHomeWorkOutlined";
import {
  type GridColDef,
  type GridRenderCellParams,
} from "@mui/x-data-grid-pro";
import { format, parseISO } from "date-fns";
import type { RegistryChangeAlertEntity } from "@/features/registryChangeAlert/types";
import { DismissButton } from "@/features/registryChangeAlert/components/DismissButton";
import { NewChip } from "@/features/registryChangeAlert/components/NewChip";
import { SectionChip } from "@/features/registryChangeAlert/components/SectionChip";

type RenderParams = GridRenderCellParams<unknown, RegistryChangeAlertEntity>;

/** 入手日（YYYY-MM-DD）を画面表示用（YYYY/MM/DD）にする。 */
export const formatReceivedDate = (value: string | null | undefined): string =>
  value ? format(parseISO(value), "yyyy/MM/dd") : "-";

/** 「甲区（所有権）」→「甲区」。補足の括弧は省き、全文はツールチップで見せる。 */
export const toShortSectionLabel = (label: string): string =>
  label.replace(/（.*$/, "");

/** 変動区分セル。区分ごとにチップで並べる（幅が足りなければ折り返す）。 */
const SectionsCell: React.FC<{ alert: RegistryChangeAlertEntity }> = ({
  alert,
}) => (
  <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ py: 0.5 }}>
    {alert.changedSections.map((section) => (
      <SectionChip key={section.value} label={section.label} />
    ))}
  </Stack>
);

/**
 * 変動アラート一覧の列定義。
 * サイドメニューを開いた 1280px 幅の画面でも横スクロールなしで操作列まで収まる幅にしている
 * （変動の区分名は「甲区」「乙区」の短いバッジで出し、全文はツールチップで見せる）。
 * @param onAcquire 行の「リユース取得」
 * @param onDismiss 行の「非表示」
 * @param isReadOnly 読み取り専用ユーザーか（「非表示」を押せなくする）
 */
export const makeRegistryChangeAlertColumns = (
  onAcquire: (alert: RegistryChangeAlertEntity) => void,
  onDismiss: (alert: RegistryChangeAlertEntity) => void,
  isReadOnly: boolean
): GridColDef[] => [
  {
    field: "pictureTypeLabel",
    headerName: "種類",
    width: 108,
    sortable: false,
    valueGetter: (params: RenderParams) =>
      `${params.row.pictureTypeLabel}／${params.row.realEstateTypeLabel}`,
    // 未読（初回表示）のときだけ NEW を添える。専用の列は設けない。
    renderCell: (params: RenderParams) => (
      <Box sx={{ py: 0.5 }}>
        {!params.row.isRead && <NewChip />}
        {/* 「全部事項／」と「土地」の間でだけ折り返す（「土」「地」の途中で折れない）。 */}
        <Box component="span" sx={{ display: "inline-block" }}>
          {`${params.row.pictureTypeLabel}／`}
        </Box>
        <Box component="span" sx={{ display: "inline-block" }}>
          {params.row.realEstateTypeLabel}
        </Box>
      </Box>
    ),
  },
  {
    field: "location",
    headerName: "所在",
    flex: 1,
    minWidth: 100,
    sortable: false,
  },
  {
    field: "chibanKaokuNumber",
    headerName: "地番・家屋番号",
    width: 124,
    sortable: false,
  },
  {
    field: "baseReceivedDate",
    headerName: "取得済み",
    width: 100,
    sortable: false,
    valueFormatter: ({ value }: { value: string }) => formatReceivedDate(value),
  },
  {
    field: "newerReceivedDate",
    headerName: "新しい登記",
    width: 100,
    sortable: false,
    valueFormatter: ({ value }: { value: string }) => formatReceivedDate(value),
  },
  {
    field: "changedSections",
    headerName: "変動",
    width: 96,
    minWidth: 96,
    sortable: false,
    renderCell: (params: RenderParams) => <SectionsCell alert={params.row} />,
  },
  {
    field: "actions",
    headerName: "",
    width: 96,
    sortable: false,
    renderCell: (params: RenderParams) => (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Tooltip title="リユース取得">
          <IconButton
            size="small"
            aria-label="リユース取得"
            onClick={() => {
              onAcquire(params.row);
            }}
          >
            <AddHomeWorkOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <DismissButton
          isReadOnly={isReadOnly}
          onClick={() => {
            onDismiss(params.row);
          }}
        />
      </Box>
    ),
  },
];
